/**
 * "Recently worked on" — the folded group on top of both inboxes (email
 * drafts and documents): every review the owner acted on (a finish or a
 * draft comment) in the last 14 days, the newest action first, with where it
 * stands now. Finished ones (closed, sent, rejected, withdrawn) stay hidden
 * until "Show finished". The rows are copies – each review also shows in its
 * own queue below – so they stay out of the keyboard navigation. The × takes
 * a row out (with Undo on the page).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

import type { AdminReviewDoc } from "../../api/admin";
import { humanizeDuration } from "../../lib/aiTeam";
import { serverTime } from "../../lib/emailDrafts";
import { backForReview, isFinished, recentStatus } from "../../lib/recentWork";
import { Pill } from "../Pill";

interface Props {
  rows: AdminReviewDoc[];
  linkTo: (doc: AdminReviewDoc) => string;
  title: (doc: AdminReviewDoc) => string;
  /** The mono line under the title – recipients, a path. */
  detail: (doc: AdminReviewDoc) => string;
  onRemove: (doc: AdminReviewDoc) => void;
  removingId?: string | null;
  className?: string;
}

export function RecentlyWorkedSection({ rows, linkTo, title, detail, onRemove, removingId, className }: Props) {
  const [open, setOpen] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const [nowMs] = useState(() => Date.now());
  if (rows.length === 0) return null;
  const finished = rows.filter(isFinished).length;
  // The server lists open rows before finished ones; mixed, newest action first.
  const shown = showFinished
    ? [...rows].sort((a, b) => serverTime(b.owner_worked_at) - serverTime(a.owner_worked_at))
    : rows.filter((doc) => !isFinished(doc));
  const back = rows.filter(backForReview).length;
  return (
    <section className={clsx("panel panel--flush", className)}>
      <div className="flex items-center">
        <button
          type="button"
          className="queue-group-toggle flex-1 min-w-0"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span aria-hidden>{open ? "▾" : "▸"}</span>
          <span className="font-semibold">Recently worked on</span>
          <span className="font-mono text-xs text-ink-low">{shown.length}</span>
          {back ? <Pill value={`${back} back for review`} tone="signal" /> : null}
          <span className="text-xs text-ink-low ml-auto shrink-0">your last actions, newest first</span>
        </button>
        {finished ? (
          <button
            type="button"
            className={clsx("btn btn-sm shrink-0 mr-4", showFinished ? "btn-main" : "btn-ghost")}
            aria-pressed={showFinished}
            title="Nothing left to do: closed, sent, rejected or withdrawn"
            onClick={() => {
              if (!showFinished) setOpen(true);
              setShowFinished(!showFinished);
            }}
          >
            Show finished <span className="tnum">{finished}</span>
          </button>
        ) : null}
      </div>
      {open ? (
        shown.length ? (
          <ul className="divide-y divide-rule border-t border-rule">
            {shown.map((doc) => {
              const status = recentStatus(doc);
              const worked = serverTime(doc.owner_worked_at);
              return (
                <li key={`recent:${doc.id}`} className="flex items-center gap-3 px-4 py-2">
                  <Link to={linkTo(doc)} className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink hover:text-accent">{title(doc)}</span>
                    <span className="block truncate font-mono text-xs text-ink-low">{detail(doc)}</span>
                  </Link>
                  {doc.agent_slug ? (
                    <span className="font-mono text-xs text-ink-low">{doc.agent_slug}</span>
                  ) : null}
                  {doc.content_version > 1 ? (
                    <span className="text-xs text-ink-low">v{doc.content_version}</span>
                  ) : null}
                  <Pill value={status.label} tone={status.tone} />
                  <span className="font-mono text-xs text-ink-low" title={doc.owner_worked_at ?? undefined}>
                    you · {worked ? `${humanizeDuration((nowMs - worked) / 1000)} ago` : "–"}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-icon"
                    aria-label={`Remove "${title(doc)}" from Recently worked on`}
                    title="Remove from Recently worked on"
                    disabled={removingId === doc.id}
                    onClick={() => onRemove(doc)}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="border-t border-rule px-4 py-2 text-sm text-ink-low">Nothing open – {finished} finished.</p>
        )
      ) : null}
    </section>
  );
}
